import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DropdownOption } from '~/components/Dropdown/types/DropdownOption';
import { createCollection } from '~/components/Dropdown/utils/createCollection';

import type { DropdownContextType } from './DropdownContext';
import { DropdownContext } from './DropdownContext';

export interface DropdownContextProviderProps<
  TValue extends string,
  TMultiselect extends boolean | undefined,
> {
  children: React.ReactNode;
  options: DropdownOption<TValue>[];
  /**
   * Optional selected options used as fallback for values that are
   * currently missing from options (e.g. remote search/pagination).
   */
  selectedOptions?: DropdownOption<TValue>[];

  /**
   * Used to display only subset of options in dropdown
   * This can be useful for search or filtering
   * @default options
   */
  filteredOptions?: DropdownOption<TValue>[];
  /**
   * If set to false, only "10 selected" will be shown
   * If set to true, all tags will be shown with the ability to remove them
   * Only works when `multiselect` is true
   */
  displaySelectedOptions?: TMultiselect extends true ? boolean : never;
  multiselect?: TMultiselect;
  value?: TMultiselect extends true ? TValue[] : TValue;
  onChange?: (selected: TMultiselect extends true ? TValue[] : TValue) => void;
  inputRef?: React.Ref<HTMLInputElement>;

  /**
   * If true, only specified number of options can be selected
   */
  maxSelectedOptions?: number;

  /**
   * Called when the user scrolls to the bottom of the dropdown
   * Useful for implementing infinite scroll or loading more options
   * @default undefined
   */
  onLoadMore?: () => Promise<void>;

  /**
   * Distance in pixels from the bottom of the dropdown to trigger `onLoadMore`
   * @default 50
   */
  loadMoreDistance?: number;

  /**
   * Text to show when there are no items in the dropdown
   */
  noOptionsText?: string;

  /**
   * Loading state, useful for async options loading
   * @default false
   */
  loading?: boolean;

  /**
   * If true, limits selected tags to few rows with vertical scrolling
   * Only applies when displaySelectedOptions is true
   */
  limitDisplayRows?: boolean;
}

export function DropdownContextProvider<
  TValue extends string,
  TMultiselect extends boolean | undefined,
>({
  children,
  options: initialOptions,
  selectedOptions: initialSelectedOptions,
  filteredOptions: initialFilteredOptions,
  multiselect,
  displaySelectedOptions,
  onChange,
  value: initialValue,
  inputRef,
  maxSelectedOptions,
  onLoadMore,
  loadMoreDistance = 50,
  noOptionsText,
  loading = false,
  limitDisplayRows,
}: DropdownContextProviderProps<TValue, TMultiselect>) {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<DropdownOption<TValue>[]>([]);
  const filteredOptions = initialFilteredOptions ?? initialOptions ?? options;
  const collection = useMemo(() => createCollection(options), [options]);
  const filteredCollection = useMemo(
    () => createCollection(filteredOptions),
    [filteredOptions],
  );
  const [selected, setSelected] = useState<TValue[]>([]);

  useEffect(() => {
    if (multiselect) {
      setSelected((initialValue as TValue[]) ?? []);
    } else {
      setSelected(initialValue ? [initialValue as TValue] : []);
    }
  }, [initialValue, multiselect]);

  useEffect(() => {
    setOptions(initialOptions);
    setSelected((prevSelectedValue) => {
      if (initialOptions.length === 0) {
        return prevSelectedValue;
      }

      if (!multiselect) {
        const singleInitialValue = initialValue as TValue | undefined;

        if (singleInitialValue != null && singleInitialValue !== '') {
          if (initialOptions.some((o) => o.value === singleInitialValue)) {
            return [singleInitialValue];
          }
        }
      }

      return prevSelectedValue;
    });
  }, [initialOptions, initialValue, multiselect]);

  const isSelected = useCallback(
    (item: TValue) => selected.includes(item),
    [selected],
  );

  const onSelect = useCallback(
    (item: TValue) => {
      let newValue: TValue[];

      if (multiselect) {
        if (selected.includes(item)) {
          newValue = selected.filter((i) => i !== item);
        } else {
          newValue = [...selected, item];
        }

        (onChange as (val: TValue[]) => void)?.(newValue);
      } else {
        if (selected[0] === item) {
          newValue = [];
        } else {
          newValue = [item];
        }

        (onChange as (val: TValue) => void)?.(newValue[0]);
        setIsOpen(false);
      }

      setSelected(newValue);
    },
    [multiselect, onChange, selected],
  );

  const selectedDropdownItems = useMemo(() => {
    return selected
      .map((value) => {
        const selectedFromOptions = collection.find((v) => v.value === value);

        if (selectedFromOptions) {
          return selectedFromOptions;
        }

        return initialSelectedOptions?.find((v) => v.value === value);
      })
      .filter(Boolean) as DropdownOption<TValue>[];
  }, [collection, initialSelectedOptions, selected]);

  const providerValue: DropdownContextType<TValue> = {
    collection: filteredCollection,
    setOptions,
    isOpen,
    setIsOpen,
    selected: selectedDropdownItems,
    onSelect,
    multiselect: Boolean(multiselect),
    displaySelectedOptions:
      Boolean(multiselect) && Boolean(displaySelectedOptions),
    isSelected,
    inputRef,
    maxSelectedOptions,
    onLoadMore,
    loadMoreDistance,
    noOptionsText,
    loading,
    limitDisplayRows,
  };

  return (
    <DropdownContext.Provider value={providerValue}>
      {children}
    </DropdownContext.Provider>
  );
}

DropdownContextProvider.displayName = 'DropdownContextProvider';
